import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { connect } from 'redux-bundler-react';
import { createColumnHelper } from '@tanstack/react-table';
import { Accordion, Alert, Label } from '@trussworks/react-uswds';

import { EditCell } from '@components/table/tableCellComponents/EditCell';
import { TableCell } from '@components/table/tableCellComponents/TableCell';
import { PermitDateTableCell } from '@components/table/tableCellComponents/PermitDateTableCell';

import TextAreaInput from '@components/textarea/TextArea';
import SelectInput from '@components/select/Select';
import TanStackTableNew from '@components/table/TanStackTable/TanStackTableNew';
import ExternalLink from '@components/external-link/ExternalLink';
import DateRange from '@components/date-range/DateRange';
import ErrorSummary from '@components/error-summary/ErrorSummary';
import Checkbox from '@components/checkbox/Checkbox';

import PermitsDredgeProject from './PermitsFormGeneralProjectInformation.dredgeProject';
import PermitsDredgeTransportation from './PermitsFormGeneralProjectInformation.dredgeTransportation';
import PermitsDischarges from './PermitsFormGeneralProjectInformation.discharges';

import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, FormProvider } from 'react-hook-form';
import { useRequest, useJpa } from '@stores';
import { useResetDirtyFields } from '@hooks';
import { useLookupTableDivisionsAndDistricts } from '@api/services/lookup/hooks';

import _isEqual from 'lodash/isEqual';

import { setNumberValue } from '@src/utils/regex';
import { ApplicableStatutoryAuthorities } from '@src/utils/enums';
import { dateBeforeA, dateBeforeB, filterNullEmptyObjects, formatCoordFlt, isDateValid } from '@src/utils/helpers';

import MultiSelectInput from '@src/app-components/multi-select';
import Tooltip from '@src/app-components/tooltip/tooltip';
import ExplanationTableCell from '@src/app-components/table/tableCellComponents/ExplanationTableCell';
import {
  developmentTypeOptions,
  nwpTermsAccordionList,
  permitsTooltipText,
  RequestedPermitTypes,
  requestedPermitTypesOptions,
} from './PermitsForm.helper';
import Section from '@components/section/Section';
import StepHeader from '@src/app-components/step-header/StepHeader';
import {
  getPermitDefaultValues,
  otherNwpValidationSchema,
  permitSchema,
  relatedAppsValidationSchema,
} from './PermitInformation.validation';

import { PermitsFormAdjoiningPropertyOwnersMetadata } from '../PermitsFormAdjoiningPropertyOwners';

export const PermitInformationMetadata = {
  sectionName: 'Permit Information',
};

const currentEnv = import.meta.env.VITE_ENVIRONMENT;

const yesNoOptions = [
  { text: 'Yes', value: 'true' },
  { text: 'No', value: 'false' },
];

const envBaseUrlMapping = {
  local: '',
  development: '',
  test: '/rrs',
  test_sf: '/rrssf',
};

const PermitInformation = connect(
  'doUpdatePermitRequest',
  'selectRequestFormData',
  'selectRequestAPIData',
  'selectGPPermitTypes',
  ({ doUpdatePermitRequest, requestFormData, requestAPIData, gPPermitTypes: allGpPermitTypes, stepNo }) => {
    const lookupTableDivisionsAndDistricts = useLookupTableDivisionsAndDistricts();

    // GP State
    const [otherNwpData, setOtherNwpData] = useState([]);
    const [otherNwpKey, setOtherNwpKey] = useState(0);
    const [otherNwpTableInvalid, setOtherNwpTableInvalid] = useState(true);
    const [otherNwpTableIsDirty, setOtherNwpTableIsDirty] = useState(false);
    const [otherNwpRowErrors, setOtherNwpRowErrors] = useState(false);
    const prevOtherNwpDataRef = useRef([]);

    // IP State
    const [relatedAppsData, setRelatedAppsData] = useState([]);
    const [relatedAppsDataIsDirty, setRelatedAppsDataIsDirty] = useState(false);
    const [relatedAppsKey, setRelatedAppsKey] = useState(0);
    const [relatedAppsRowErrors, setRelatedAppsRowErrors] = useState(false);
    const prevRelatedAppsDataRef = useRef([]);

    // Common State
    const [dredgeProjectData, setDredgeProjectData] = useState({});
    const columnHelper = createColumnHelper();
    const relatedAppsTableLength = relatedAppsData?.length;
    const otherNwpTableLength = otherNwpData?.length;
    const district =
      lookupTableDivisionsAndDistricts?.filter((item) => item.orgName === requestFormData?.location?.district)?.[0] ??
      null;

    const defaultValues = useMemo(
      () => getPermitDefaultValues({ permitData: requestAPIData?.request?.permits?.[0], district }),
      [requestAPIData?.request?.permits, district]
    );

    const schema = useMemo(() => permitSchema, []);

    const methods = useForm({
      defaultValues: defaultValues,
      mode: 'onBlur',
      resolver: yupResolver(schema),
      context: {
        otherNwpTableLength: otherNwpTableLength,
        otherNwpRowErrors: otherNwpRowErrors,
        relatedAppsTableLength: relatedAppsTableLength,
      },
    });
    const {
      formState: { errors, dirtyFields },
      watch,
      getValues,
      setError,
      clearErrors,
      trigger,
      reset,
    } = methods;
    const { updateSection, isFormReadOnly: isReadOnly, isSectionTouched, sections } = useRequest();
    const { validPermitTypes } = useJpa();

    const requestedPermitType = watch('requestedPermitType');
    const gpPermitTypes = watch('gpPermitTypes');
    const isAnyWorkComplete = watch('isAnyWorkComplete');
    const applicableStatutoryAuthority = watch('applicableStatutoryAuthority');
    const isDredgingNavWaters = watch('isDredgingNavWaters');
    const hasRelatedAgencyApplications = watch('hasRelatedAgencyApplications');
    const isInTerrSeasOrOceanWaters = watch('isInTerrSeasOrOceanWaters');
    const isCoastalDistrict = watch('isCoastalDistrict');

    const isDirty = Object.keys(dirtyFields).length > 0 || relatedAppsDataIsDirty || otherNwpTableIsDirty;
    const relatedAppsValid =
      hasRelatedAgencyApplications === 'true' ? relatedAppsData?.length > 0 && !relatedAppsRowErrors : true;
    const isValid = Object.keys(errors).length === 0 && relatedAppsValid && !otherNwpTableInvalid;
    const isTouched = isSectionTouched(stepNo);

    const isNwp27 = gpPermitTypes?.some((item) => item?.label?.includes('NWP 27'));

    const getJpaSectionIds = useCallback(() => {
      const parentSections = sections.filter((section) => {
        return section.label === 'Location Specific Information' || section.label === 'Location Specific Questions';
      });

      const parentSectionIds = parentSections.map((section) => section.id);
      const childSections = sections.filter((section) => parentSectionIds.includes(section.parentId));
      const childSectionIds = childSections.map((section) => section.id);
      const childDependentSections = sections.filter((section) => childSectionIds.includes(section.parentId));
      const childDependentSectionIds = childDependentSections.map((section) => section.id);

      return [...parentSectionIds, ...childSectionIds, ...childDependentSectionIds];
    }, [sections]);

    // Indicator to display IP vs GP related questions - determine if requestedPermitType is IP vs GP
    const isGp = requestedPermitType === RequestedPermitTypes.GeneralPermit;
    const updateTableDataFn = isGp ? setOtherNwpData : setRelatedAppsData;
    const updateTableKeyFn = isGp ? setOtherNwpKey : setRelatedAppsKey;

    const otherNwpColumns = useMemo(
      () => [
        columnHelper.display({
          id: 'edit',
          cell: isReadOnly ? <></> : EditCell,
          size: 40,
          enableResizing: false,
          enableSorting: false,
        }),
        columnHelper.accessor('approvalType', {
          header: 'Type of Approval',
          cell: isReadOnly ? ({ cell }) => <span>{cell.getValue()}</span> : TableCell,
          size: 275,
          meta: {
            type: 'select',
            required: true,
            options: [
              { value: 'Nationwide Permit', label: 'Nationwide Permit' },
              { value: 'Regional General Permit', label: 'Regional General Permit' },
              { value: 'Programmatic General Permit', label: 'Programmatic General Permit' },
              { value: 'Individual Permit', label: 'Individual Permit' },
              { value: 'Other', label: 'Other' },
            ],
          },
        }),
        columnHelper.accessor('explanation', {
          header: 'Explanation',
          cell: isReadOnly ? ({ cell }) => <span>{cell.getValue()}</span> : ExplanationTableCell,
          size: 300,
          meta: {
            required: true,
            type: 'text',
          },
        }),
        columnHelper.accessor('fileNumber', {
          header: 'File Number',
          cell: isReadOnly ? ({ cell }) => <span>{cell.getValue()}</span> : TableCell,
          size: 300,
          meta: {
            required: true,
            type: 'text',
          },
        }),
        columnHelper.accessor('appliedDate', {
          header: 'Date Applied',
          cell: isReadOnly ? ({ cell }) => <span>{cell.getValue()}</span> : PermitDateTableCell,
          size: 150,
          meta: {
            type: 'date',
            min: dateBeforeB,
            required: true,
          },
        }),
        columnHelper.accessor('approvedDate', {
          header: 'Date Approved',
          cell: isReadOnly ? ({ cell }) => <span>{cell.getValue()}</span> : PermitDateTableCell,
          size: 150,
          meta: {
            type: 'date',
            min: dateBeforeB,
            required: false,
          },
        }),
        columnHelper.accessor('deniedDate', {
          header: 'Date Denied',
          cell: isReadOnly ? ({ cell }) => <span>{cell.getValue()}</span> : PermitDateTableCell,
          size: 150,
          meta: {
            type: 'date',
            min: dateBeforeB,
            required: false,
          },
        }),
      ],
      [columnHelper, isReadOnly]
    );

    const relatedAppsColumns = useMemo(
      () => [
        columnHelper.display({
          id: 'edit',
          cell: isReadOnly ? <></> : EditCell,
          size: 40,
          enableResizing: false,
          enableSorting: false,
        }),
        columnHelper.accessor('agency', {
          header: 'Agency Name',
          cell: isReadOnly ? ({ cell }) => <span>{cell.getValue()}</span> : TableCell,
          size: 400,
          meta: {
            required: true,
          },
        }),
        columnHelper.accessor('approvalType', {
          header: 'Type of Approval',
          cell: isReadOnly ? ({ cell }) => <span>{cell.getValue()}</span> : TableCell,
          size: 380,
          meta: {
            required: true,
          },
        }),
        columnHelper.accessor('certIDNumber', {
          header: 'Identification Number',
          cell: isReadOnly ? ({ cell }) => <span>{cell.getValue()}</span> : TableCell,
          size: 300,
          meta: {
            required: true,
            maxLength: 100,
          },
        }),
        columnHelper.accessor('appliedDate', {
          header: 'Date Applied',
          cell: isReadOnly ? ({ cell }) => <span>{cell.getValue()}</span> : PermitDateTableCell,
          size: 150,
          meta: {
            type: 'date',
            min: dateBeforeB,
            required: true,
          },
        }),
        columnHelper.accessor('approvedDate', {
          header: 'Date Approved',
          cell: isReadOnly ? ({ cell }) => <span>{cell.getValue()}</span> : PermitDateTableCell,
          size: 150,
          meta: {
            type: 'date',
            min: dateBeforeB,
            required: false,
          },
        }),
        columnHelper.accessor('deniedDate', {
          header: 'Date Denied',
          cell: isReadOnly ? ({ cell }) => <span>{cell.getValue()}</span> : PermitDateTableCell,
          size: 150,
          cellDisabled: true,
          meta: {
            type: 'date',
            min: dateBeforeB,
            required: false,
          },
        }),
      ],
      [columnHelper, isReadOnly]
    );

    const buildGpPermitTypeOptions = (permitTypes = []) => {
      const valueCounts = permitTypes.reduce((acc, item) => {
        acc[item.value] = (acc[item.value] || 0) + 1;
        return acc;
      }, {});

      return permitTypes.map((item) => {
        const isDuplicate = valueCounts[item.value] > 1;
        const label = isDuplicate && item.permitType ? `${item.value} (${item.permitType})` : item.value;

        return {
          value: String(item.permitTypeID),
          label,
        };
      });
    };

    const getVisibleGpPermitTypeOptions = (options = [], selectedOptions = []) => {
      const notSureOption = options.find((option) => option.value === '1');
      if (!notSureOption) {
        return options;
      }
      const hasNotSureSelected = selectedOptions.some((option) => option.value === '1');
      const hasPermitType = selectedOptions.some((option) => option.value !== '1');
      if (hasNotSureSelected) {
        return [notSureOption];
      }
      if (hasPermitType) {
        return options.filter((option) => option.value !== '1');
      }
      return options;
    };

    const gpPermitTypeOptions = useMemo(() => {
      const mappedOptions = buildGpPermitTypeOptions(allGpPermitTypes);
      return getVisibleGpPermitTypeOptions(mappedOptions, gpPermitTypes);
    }, [allGpPermitTypes, gpPermitTypes]);

    const addRow = useCallback(() => updateTableDataFn((prev) => (prev ? [...prev, {}] : [{}])), [updateTableDataFn]);

    const updateSourceData = useCallback(
      (rowIndex, columnId, updatedValue) => {
        updateTableDataFn((oldData) => {
          const newData = oldData ? [...oldData] : null;
          if (newData && newData[rowIndex]) {
            // Update properties
            newData[rowIndex] = {
              ...newData[rowIndex],
              [columnId]: updatedValue,
            };
            return newData;
          }
        });
      },
      [updateTableDataFn]
    );

    const removeMultipleRows = useCallback(
      (indicesToRemove) => {
        updateTableDataFn((oldData) => {
          const newRows = oldData.filter((_, index) => !indicesToRemove.includes(index));
          return newRows;
        });
        updateTableKeyFn((old) => old + 1);
      },
      [updateTableDataFn, updateTableKeyFn]
    );

    // Populate General Project Info nested sections

    const populateDredgeProject = useCallback(() => {
      const values = getValues();
      // Dredge Project
      const dredgeProjectObj = {
        materialTypeComposition: values?.materialTypeComposition ?? '',
        dredgeMethod: values?.dredgeMethod ?? '',
        constructionMethods: values?.constructionMethods ?? '',
        estimatedNumberOfEvents: setNumberValue(values?.estimatedNumberOfEvents),
        workStartDate: isDateValid(values?.workStartDate) ? values?.workStartDate : '',
        workEndDate: isDateValid(values?.workEndDate) ? values?.workEndDate : '',
        durationOfWork: setNumberValue(values?.durationOfWork),
        unitOfWork: values?.unitOfWork ?? '',
        currentDepth: setNumberValue(values?.currentDepth, false),
        isDredgeTypeMaintenance:
          values?.dredgeType?.length > 0 ? (values?.dredgeType?.includes('Maintenance') ? true : false) : null,
        isDredgeTypeNew: values?.dredgeType?.length > 0 ? (values?.dredgeType?.includes('New') ? true : false) : null,
        lastDredgeCycle: values?.lastDredgeCycle ?? '',
        authorizedLength: setNumberValue(values?.authorizedLength, false),
        authorizedWidth: setNumberValue(values?.authorizedWidth, false),
        authorizedDepth: setNumberValue(values?.authorizedDepth, false),
        disposalPlan: values?.disposalPlan ?? '',
        estNumDisposalTrips: setNumberValue(values?.estNumDisposalTrips),
        dredgeDisposalLatitude: formatCoordFlt(values?.dredgeDisposalLatitude) ?? null,
        dredgeDisposalLongitude: formatCoordFlt(values?.dredgeDisposalLongitude) ?? null,
      };
      // Check if values in obj are all null/empty string
      return filterNullEmptyObjects(dredgeProjectObj) !== null ? { ...dredgeProjectData, ...dredgeProjectObj } : null;
    }, [getValues, dredgeProjectData]);

    const populateDischarges = useCallback(() => {
      const values = getValues();
      const discharges404Obj = {
        materialSource: values?.dischargeMaterialSource ?? '',
        dischargeReason: values?.dischargeReason ?? '',
      };
      return filterNullEmptyObjects(discharges404Obj) !== null ? { ...discharges404Obj } : null;
    }, [getValues]);

    const populateDredgedTransportation = useCallback(() => {
      const values = getValues();
      const dredgedTransportationObj = {
        isSiteDesignatedForDISP:
          values?.isSiteDesignatedForDISP === 'true' ? true : values?.isSiteDesignatedForDISP === 'false' ? false : '',
        knownDredgeMaterialDischarges: values?.knownDredgeMaterialDischarges ?? '',
        disposalSiteCharacteristics: values?.disposalSiteCharacteristics ?? '',
        previousDisposals: values?.previousDisposals ?? '',
        estNumberOfMonthsOnSite: setNumberValue(values?.estNumberOfMonthsOnSite),
        materialSource: values?.materialSource ?? '',
        materialTypeAndComp: values?.materialTypeAndComp ?? '',
        transportationAndDisposalMethod: values?.transportationAndDisposalMethod ?? '',
      };
      return filterNullEmptyObjects(dredgedTransportationObj) !== null ? { ...dredgedTransportationObj } : null;
    }, [getValues]);

    const mapGpPermitTypesForSave = (gpPermitTypes = [], allGpPermitTypes = []) =>
      gpPermitTypes.map((item) => {
        const matchedPermit = allGpPermitTypes.find((permit) => String(permit.permitTypeID) === item?.value);

        return {
          permitTypeID: matchedPermit?.permitTypeID,
          value: matchedPermit?.value,
        };
      });

    const handleChange = useCallback(
      (e) => {
        if (e?.target?.name === 'dredgeType') trigger();

        if (!isReadOnly) {
          // Map & insert values into Permits data modal
          const values = getValues();
          let permitValues;
          // Common data model object for GP and IP
          const commonValues = {
            requestedPermitType: values?.requestedPermitType ?? '',
            navigableWatersAdditionalInfo: values?.navigableWatersAdditionalInfo ?? '',
            isAnyWorkComplete:
              values?.isAnyWorkComplete === 'true' ? true : values?.isAnyWorkComplete === 'false' ? false : null,
            completedWorkDescription: values?.completedWorkDescription ?? '',
            isDredgingNavWaters:
              values?.isDredgingNavWaters === 'true' ? true : values?.isDredgingNavWaters === 'false' ? false : null,
            includesInstallPiling: false,
            includesShorelineStabilization: false,
            applicableStatutoryAuthorities: values?.applicableStatutoryAuthority ?? [],
            isInTerrSeasOrOceanWaters:
              values?.isInTerrSeasOrOceanWaters === 'true'
                ? true
                : values?.isInTerrSeasOrOceanWaters === 'false'
                  ? false
                  : null,
            activityBaselineRelationship: values?.activityBaselineRelationship ?? '',
            dredgeProject: populateDredgeProject() ?? null,
          };

          if (isGp) {
            permitValues = {
              activityDescription: values?.activityDescription ?? '',
              activityPurposeAndNeed: values?.activityPurposeAndNeed ?? '',
              gpPermitTypes: mapGpPermitTypesForSave(values?.gpPermitTypes, allGpPermitTypes),
            };
          } else {
            permitValues = {
              hasRelatedAgencyApplications:
                values?.hasRelatedAgencyApplications === 'true'
                  ? true
                  : values?.hasRelatedAgencyApplications === 'false'
                    ? false
                    : null,
              developmentType: values?.developmentType ?? '',
              workStartDate: isDateValid(values?.projectWorkStartDate) ? values?.projectWorkStartDate : '',
              workEndDate: isDateValid(values?.projectWorkEndDate) ? values?.projectWorkEndDate : '',
              isInTerrSeasOrOceanWaters:
                values?.isInTerrSeasOrOceanWaters === 'true'
                  ? true
                  : values?.isInTerrSeasOrOceanWaters === 'false'
                    ? false
                    : null,
              activityBaselineRelationship: values?.activityBaselineRelationship ?? '',
              discharges_404: populateDischarges() ?? null,
              dredgedTransportation: populateDredgedTransportation() ?? null,
            };
          }
          doUpdatePermitRequest({ ...commonValues, ...permitValues });
        }
      },
      [
        trigger,
        isReadOnly,
        getValues,
        populateDredgeProject,
        isGp,
        doUpdatePermitRequest,
        allGpPermitTypes,
        populateDischarges,
        populateDredgedTransportation,
      ]
    );

    // Reset defaultValues on save
    useEffect(() => {
      reset(defaultValues, {
        keepDirty: true,
        keepErrors: true,
      });
    }, [defaultValues, reset]);

    // Dynamic validation schema based on requestedPermitType
    useEffect(() => {
      if (requestedPermitType) {
        isTouched && trigger();
      }
    }, [requestedPermitType, isTouched, trigger]);

    useEffect(() => {
      trigger('otherNwp');
    }, [otherNwpTableLength, otherNwpRowErrors, trigger]);

    useEffect(() => {
      trigger('relatedAppsTable');
    }, [relatedAppsTableLength, trigger]);

    // Load data from API
    useEffect(() => {
      if (requestAPIData?.request?.permits?.[0]?.relatedAgencyApplications) {
        const relatedAppsData = requestAPIData?.request?.permits?.[0]?.relatedAgencyApplications.map((item) => ({
          ...item,
          appliedDate: isDateValid(item?.appliedDate) ? item?.appliedDate?.split('T')?.[0] : '',
          approvedDate: isDateValid(item?.approvedDate) ? item?.approvedDate?.split('T')?.[0] : '',
          deniedDate: isDateValid(item?.deniedDate) ? item?.deniedDate?.split('T')?.[0] : '',
        }));
        setRelatedAppsData(relatedAppsData);
        prevRelatedAppsDataRef.current = relatedAppsData;
      }

      if (requestAPIData?.request?.permits?.[0]?.supportingPermits) {
        const otherNWPData = requestAPIData?.request?.permits?.[0]?.supportingPermits.map((item) => ({
          ...item,
          appliedDate: isDateValid(item?.appliedDate?.split('T')?.[0]) ? item?.appliedDate?.split('T')?.[0] : '',
          approvedDate: isDateValid(item?.approvedDate?.split('T')?.[0]) ? item?.approvedDate?.split('T')?.[0] : '',
          deniedDate: isDateValid(item?.deniedDate?.split('T')?.[0]) ? item?.deniedDate?.split('T')?.[0] : '',
        }));

        setOtherNwpData(otherNWPData);
        prevOtherNwpDataRef.current = otherNWPData;
      }

      if (requestAPIData?.request?.permits?.[0]?.dredgeProject) {
        setDredgeProjectData(requestAPIData?.request?.permits?.[0]?.dredgeProject);
      }
      isTouched && trigger();
    }, [requestAPIData, isTouched, trigger]);

    // Handle Table Data
    useEffect(() => {
      const tableDataChanged = !_isEqual(relatedAppsData, prevRelatedAppsDataRef.current);
      tableDataChanged && setRelatedAppsDataIsDirty(true);
    }, [relatedAppsData]);

    useEffect(() => {
      const tableDataChanged = !_isEqual(otherNwpData, prevOtherNwpDataRef.current);
      tableDataChanged && setOtherNwpTableIsDirty(true);
    }, [otherNwpData]);

    useEffect(() => {
      relatedAppsData && doUpdatePermitRequest({ relatedAgencyApplications: relatedAppsData });
    }, [relatedAppsData, doUpdatePermitRequest]);

    useEffect(() => {
      otherNwpData &&
        doUpdatePermitRequest({
          supportingPermits: otherNwpData.map((item) => ({
            ...item,
            appliedDate: isDateValid(item?.appliedDate?.split('T')?.[0]) ? item?.appliedDate?.split('T')?.[0] : '',
            approvedDate: isDateValid(item?.approvedDate?.split('T')?.[0]) ? item?.approvedDate?.split('T')?.[0] : '',
            deniedDate: isDateValid(item?.deniedDate?.split('T')?.[0]) ? item?.deniedDate?.split('T')?.[0] : '',
          })),
        });
    }, [otherNwpData, doUpdatePermitRequest]);

    // Update Dredge Project data in Permit Request Body
    useEffect(() => {
      dredgeProjectData && doUpdatePermitRequest({ dredgeProject: dredgeProjectData });
    }, [dredgeProjectData, doUpdatePermitRequest]);

    useEffect(() => {
      if (isTouched) {
        if (relatedAppsTableLength < 1 && hasRelatedAgencyApplications === 'true') {
          setError('relatedAppsTable', {
            type: 'custom',
            message: 'Previous Permits, Certificates... : At least one certificate, approval or denial is required',
          });
        } else if (relatedAppsRowErrors) {
          setError('relatedAppsTable', {
            type: 'custom',
            message: 'Previous Permits, Certificates... : Table contains invalid rows',
          });
        } else if (!relatedAppsRowErrors && relatedAppsTableLength > 0) {
          clearErrors('relatedAppsTable');
        } else {
          clearErrors('relatedAppsTable');
        }
      }
    }, [hasRelatedAgencyApplications, relatedAppsRowErrors, relatedAppsTableLength, isTouched, clearErrors, setError]);

    useEffect(() => {
      if (isTouched) {
        if (otherNwpRowErrors && otherNwpTableLength > 0) {
          setError('otherNwp', { type: 'custom', message: 'Other Nationwide Permits: Table contains invalid rows' });
          setOtherNwpTableInvalid(true);
        } else if (!otherNwpRowErrors && otherNwpTableInvalid !== false) {
          clearErrors('otherNwp');
          setOtherNwpTableInvalid(false);
        }
      }
    }, [
      otherNwpRowErrors,
      otherNwpTableLength,
      isTouched,
      setOtherNwpTableInvalid,
      setError,
      clearErrors,
      otherNwpTableInvalid,
    ]);

    useEffect(() => {
      const relatedAppsValid =
        hasRelatedAgencyApplications === 'true' ? relatedAppsData?.length > 0 && !relatedAppsRowErrors : true;
      const sectionValid = isValid && relatedAppsValid;
      updateSection(stepNo, { dirty: isDirty, valid: sectionValid });
    }, [
      isValid,
      isDirty,
      updateSection,
      hasRelatedAgencyApplications,
      stepNo,
      relatedAppsData?.length,
      relatedAppsRowErrors,
      isReadOnly,
      relatedAppsDataIsDirty,
    ]);

    const apoIndex = useMemo(
      () => sections.findIndex((section) => section.label === PermitsFormAdjoiningPropertyOwnersMetadata.sectionName),
      [sections]
    );

    // Only show adjoining property owners for Standard Permit requests
    useEffect(() => {
      if (apoIndex === -1) return;

      updateSection(apoIndex, {
        hidden: requestedPermitType !== RequestedPermitTypes.StandardPermit,
      });
    }, [requestedPermitType, updateSection, apoIndex]);

    // Hide JPA sections until valid permit type is selected
    useEffect(
      () => {
        const jpaSectionIds = getJpaSectionIds();
        const shouldShow = validPermitTypes?.includes(requestedPermitType);
        jpaSectionIds.forEach((sectionId) => {
          updateSection(sectionId, { hidden: !shouldShow });
        });
      }, // eslint-disable-next-line react-hooks/exhaustive-deps
      [requestedPermitType, validPermitTypes, updateSection]
    );

    // Reset the dirty states of the fields after a save. Only trigger validation for steps that have been touched.
    useResetDirtyFields(isTouched, requestAPIData, reset, trigger);

    return (
      <FormProvider {...methods}>
        {errors && isTouched && !isReadOnly && <ErrorSummary errors={errors} sectionNo={stepNo} />}
        <StepHeader text='Permit Information' />
        <div>
          <SelectInput
            className='width-50'
            fullWidth={false}
            name='requestedPermitType'
            label="Select the type of permit you're requesting"
            tooltip={permitsTooltipText.requestedPermitType}
            onChange={handleChange}
            readOnly={isReadOnly}
            required
          >
            {requestedPermitTypesOptions?.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </SelectInput>
        </div>
        {/* Display rest of questions if requestedPermitType has a value */}
        {requestedPermitType && (
          <>
            {isGp ? (
              <>
                <div>
                  <MultiSelectInput
                    multi
                    name='gpPermitTypes'
                    label='Identify the specific Nationwide Permit(s) or Regional or Programmatic General Permit you propose to use'
                    tooltip={permitsTooltipText.permitTypes}
                    options={gpPermitTypeOptions}
                    handleChange={handleChange}
                    readOnly={isReadOnly}
                    required
                  />
                </div>
                <Alert type='info' heading='NOTE' headingLevel='h4'>
                  You can access additional information about the Nationwide Permit(s) or Regional or Programmatic
                  General Permit you select above, at the following link:{' '}
                  <ExternalLink
                    href='https://usace.contentdm.oclc.org/utils/getfile/collection/p16021coll7/id/19757'
                    title='read more here'
                    content='https://usace.contentdm.oclc.org/utils/getfile/collection/p16021coll7/id/19757'
                  />
                </Alert>
                <Alert type='info'>
                  The PCN must include a delineation of wetlands, other special aquatic sites, and other waters, such as
                  lakes and ponds, and perennial and intermittent streams, on the project site. Wetland delineations
                  must be prepared in accordance with the current method required by USACE. The permittee may ask USACE
                  to delineate the special aquatic sites and other waters on the project site, but there may be a delay
                  if USACE does the delineation, especially if the project site is large or contains many wetlands,
                  other special aquatic sites, and other waters. Furthermore, the 45-day period will not start until the
                  delineation has been submitted to or completed by USACE, as appropriate.
                </Alert>
                {isNwp27 && (
                  <Alert type='info'>
                    You must submit a report containing information on the proposed aquatic ecosystem restoration,
                    enhancement, and establishment activity to the district engineer at least 30 days prior to
                    commencing activities in waters of the United States authorized by this NWP. Please upload a copy of
                    the report to the Supporting Information Section. Report requirements can be found under the terms
                    of Nationwide Permit 27: 2026{' '}
                    <ExternalLink href='https://usace.contentdm.oclc.org/utils/getfile/collection/p16021coll9/id/3209'>
                      Nationwide Permits, General Conditions, and Definitions.
                    </ExternalLink>
                  </Alert>
                )}
                <Label className='margin-top-2 margin-bottom-0'>
                  List any other Nationwide Permit(s), Regional General Permit(s), Individual Permit(s) used to
                  authorize any part of the proposed project or any related activity. Also, if any other Certificates or
                  Approvals/Denials have been received from other Federal, State or Local Agencies for Work Described in
                  This Application, please include here and select “Other” in the Type of Approval column{' '}
                  <Tooltip
                    name='NWP-tooltip'
                    content={permitsTooltipText.otherNwp}
                    clickable={false}
                    iconStyle={{ marginLeft: '5px' }}
                  />
                </Label>
                <div id='otherNwp'>
                  <TanStackTableNew
                    addRow={addRow}
                    columns={otherNwpColumns}
                    data={otherNwpData}
                    hideDrag={true}
                    initialTableState={{}}
                    isInputTable={true}
                    isReadOnly={isReadOnly}
                    key={otherNwpKey}
                    placeholderClick={addRow}
                    placeholderText='No Permits found.'
                    removeMultipleRows={removeMultipleRows}
                    rowErrorCallback={setOtherNwpRowErrors}
                    updateSourceData={updateSourceData}
                    validationSchema={otherNwpValidationSchema}
                  />
                </div>
                <div className='margin-top-4 '>
                  <Label>
                    If the terms of the Nationwide Permit(s) you want to use require additional information to be
                    included in the PCN, please provide here
                  </Label>
                  <Accordion bordered items={nwpTermsAccordionList} />
                  <TextAreaInput
                    className='margin-top-1'
                    name='navigableWatersAdditionalInfo'
                    onChange={handleChange}
                    readOnly={isReadOnly}
                  />
                </div>
              </>
            ) : (
              <>
                <div>
                  <SelectInput
                    className='width-50'
                    fullWidth={false}
                    name='developmentType'
                    label='Select a development type'
                    required
                    tooltip={permitsTooltipText.developmentType}
                    onChange={handleChange}
                    readOnly={isReadOnly}
                  >
                    {developmentTypeOptions.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </SelectInput>
                </div>
                <div>
                  <SelectInput
                    className='width-50'
                    fullWidth={false}
                    name='hasRelatedAgencyApplications'
                    label='Have other Certificates or Approvals/Denials been received from other Federal, State, or Local Agencies for Work Described in This Application?'
                    required
                    tooltip={permitsTooltipText.otherCerts}
                    onChange={handleChange}
                    readOnly={isReadOnly}
                  >
                    {yesNoOptions.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.text}
                      </option>
                    ))}
                  </SelectInput>
                </div>
                {hasRelatedAgencyApplications === 'true' && (
                  <div>
                    <Label className='margin-top-2 margin-bottom-0 required'>
                      List any certificates or approvals/denials that have been received from other federal, state, or
                      local agencies for work described in this application.
                    </Label>
                    <TanStackTableNew
                      addRow={addRow}
                      columns={relatedAppsColumns}
                      data={relatedAppsData}
                      hideDrag={true}
                      initialTableState={{}}
                      isInputTable={true}
                      isReadOnly={isReadOnly}
                      key={relatedAppsKey}
                      placeholderClick={addRow}
                      placeholderText='No certificates or approvals/denials found, Please click below to add a certificate,  approvals and/or denial'
                      removeMultipleRows={removeMultipleRows}
                      rowErrorCallback={setRelatedAppsRowErrors}
                      updateSourceData={updateSourceData}
                      validationSchema={relatedAppsValidationSchema}
                    />
                  </div>
                )}
                <fieldset className='margin-top-3 border-0 padding-0' id='projectWorkDates'>
                  <Label htmlFor='projectWorkDates' className='margin-0'>
                    Approximate dates you plan to both begin and complete all work:
                  </Label>
                  <DateRange
                    nameBefore='projectWorkStartDate'
                    nameAfter='projectWorkEndDate'
                    labelBefore='Project Start Date'
                    labelAfter='Project End Date'
                    dateBefore={dateBeforeA}
                    onChange={handleChange}
                    readOnly={isReadOnly}
                    required
                  />
                </fieldset>
              </>
            )}
            <div id='relatedAppsTable'>
              <SelectInput
                className='width-50'
                fullWidth={false}
                name='isAnyWorkComplete'
                label='Is any portion of the work complete?'
                tooltip={permitsTooltipText.isAnyWorkComplete}
                onChange={handleChange}
                readOnly={isReadOnly}
                required
              >
                {yesNoOptions.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.text}
                  </option>
                ))}
              </SelectInput>
            </div>
            {isAnyWorkComplete === 'true' && (
              <div>
                <TextAreaInput
                  name='completedWorkDescription'
                  label='Describe the completed work'
                  onChange={handleChange}
                  readOnly={isReadOnly}
                  required
                />
              </div>
            )}
            <fieldset className='padding-0 margin-top-1 margin-left-0' id='applicableStatutoryAuthority'>
              <Label className='margin-bottom-0 required' htmlFor='applicableStatutoryAuthority'>
                <span id='applicableStatutoryAuthority_label'>Select Applicable Statutory Authority</span>
              </Label>
              <Alert type='info' className='margin-bottom-2'>
                If you're unsure of which authorities apply to your project, you can{' '}
                <ExternalLink
                  href={`${envBaseUrlMapping[currentEnv]}/home/permitting`}
                  title='read about permitting requirements'
                  content='read about permitting requirements'
                  aria-label='read about permitting requirements'
                />{' '}
                or contact your{' '}
                <ExternalLink
                  href='https://regulatory.ops.usace.army.mil/offices '
                  title='local regulatory office'
                  content='local regulatory office'
                  aria-label='local regulatory office'
                />
                .
              </Alert>
              <Checkbox
                disabled={isReadOnly}
                id='check-section-404'
                label={ApplicableStatutoryAuthorities.Section404}
                labelDescription='Requires authorization from the U.S. Army Corps of Engineers, for the discharge of dredged or fill material into all waters of the United States, including wetlands. Discharges of fill material generally include, without limitation: placement of fill that is necessary for the construction of any structure, or impoundment requiring rock, sand, dirt, or other material for its construction; site-development fills for recreational, industrial, commercial, residential, and other uses; causeways or road fills; dams and dikes; artificial islands; property protection or reclamation devices such as riprap, groins, seawalls, breakwaters, and revetments; beach nourishment; levees; fill for intake and outfall pipes and subaqueous utility lines; fill associated with the creation of ponds; and any other work involving the discharge of fill or dredged material. A USACE permit is required whether the work is permanent or temporary. Examples of temporary discharges include dewatering of dredged material prior to final disposal, and temporary fills for access roadways, cofferdams, storage and work areas.'
                name='applicableStatutoryAuthority'
                onChange={handleChange}
                tile
                value={ApplicableStatutoryAuthorities.Section404}
              />
              <Checkbox
                disabled={isReadOnly}
                id='check-section-10'
                label={ApplicableStatutoryAuthorities.Section10}
                labelDescription='Requires authorization from the United States Army Corps of Engineers, for the construction of any structure in or over any navigable water of the United States. Structures or work outside the limits defined for navigable waters of the United States require a Section 10 permit if the structure or work affects the course, location, or condition of the water body. The law applies to any dredging or disposal of dredged materials, excavation, filling, re-channelization, or any other modification of a navigable water of the United States, and applies to all structures, from the smallest floating dock to the largest commercial undertaking. It further includes, without limitation, any wharf, dolphin, weir, boom breakwater, jetty, groin, bank protection (e.g. riprap, revetment, bulkhead), mooring structures such as pilings, aerial or subaqueous power transmission lines, intake or outfall pipes, permanently moored floating vessel, tunnel, artificial canal, boat ramp, aids to navigation, and any other permanent, or semi-permanent obstacle or obstruction.'
                name='applicableStatutoryAuthority'
                onChange={handleChange}
                tile
                value={ApplicableStatutoryAuthorities.Section10}
              />
              {!isGp && (
                <Checkbox
                  disabled={isReadOnly}
                  id='check-section-103'
                  label={ApplicableStatutoryAuthorities.Section103}
                  labelDescription='Requires authorization from the United States Army Corps of Engineers for the disposal of dredged materials in the ocean.'
                  name='applicableStatutoryAuthority'
                  onChange={handleChange}
                  tile
                  value={ApplicableStatutoryAuthorities.Section103}
                />
              )}
            </fieldset>
            {isCoastalDistrict && (
              <>
                <div>
                  <SelectInput
                    className='width-50'
                    fullWidth={false}
                    name='isInTerrSeasOrOceanWaters'
                    label='Would the project occur in the territorial seas or ocean waters?'
                    required
                    readOnly={isReadOnly}
                    onChange={handleChange}
                  >
                    {yesNoOptions.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.text}
                      </option>
                    ))}
                  </SelectInput>
                </div>
                {isInTerrSeasOrOceanWaters === 'true' && (
                  <div>
                    <TextAreaInput
                      name='activityBaselineRelationship'
                      label="Describe the activity's relationship to the baseline from which the territorial sea is measured"
                      tooltip={permitsTooltipText.activityBaselineRelationship}
                      required
                      onChange={handleChange}
                      readOnly={isReadOnly}
                    />
                  </div>
                )}
              </>
            )}
            {/* Discharge Section - IP ONLY */}
            {applicableStatutoryAuthority?.length > 0 &&
              applicableStatutoryAuthority?.includes(ApplicableStatutoryAuthorities.Section404) &&
              !isGp && (
                <Section label='Discharges'>
                  <p className='text-bold'>Projects involving Section 404 Discharges</p>
                  <PermitsDischarges handleChange={handleChange} isReadOnly={isReadOnly} />
                </Section>
              )}
            {/* Dredge Project Section */}
            {applicableStatutoryAuthority?.length > 0 &&
              applicableStatutoryAuthority?.includes(ApplicableStatutoryAuthorities.Section10) && (
                <Section label='Dredging'>
                  <p className='text-bold'>Projects Involving Impacts to Section 10 Navigable Waters</p>

                  <div>
                    <SelectInput
                      name='isDredgingNavWaters'
                      label='Does the project involve dredging in navigable waters of the United States?'
                      onChange={handleChange}
                      readOnly={isReadOnly}
                      required
                    >
                      <option key='2' value='true'>
                        Yes
                      </option>
                      <option key='3' value='false'>
                        No
                      </option>
                    </SelectInput>
                  </div>
                  {isDredgingNavWaters === 'true' && (
                    <PermitsDredgeProject handleChange={handleChange} isReadOnly={isReadOnly} />
                  )}
                </Section>
              )}
            {/* Dredge Transportation Section - IP ONLY */}
            {applicableStatutoryAuthority?.length > 0 &&
              applicableStatutoryAuthority?.includes(ApplicableStatutoryAuthorities.Section103) &&
              !isGp && (
                <Section isSubsection label='Dredge Transportation'>
                  <p className='text-bold'>
                    Projects Involving the transportation of dredged material for the purpose of disposing of it in
                    ocean waters
                  </p>
                  <Alert type='warning'>
                    IMPORTANT: The physical boundaries of the disposal site must be provided in a map attached to this
                    request. The specific location of the disposal of the dredged material must be itemized in the
                    impact section of this request.
                  </Alert>
                  <PermitsDredgeTransportation handleChange={handleChange} isReadOnly={isReadOnly} />
                </Section>
              )}
          </>
        )}
      </FormProvider>
    );
  }
);

PermitInformation.metadata = PermitInformationMetadata;

export default PermitInformation;
